<template>
    <div class="page">
        <v-container :class="{'px-0': phoneOnly}">
            <v-card :flat="phoneOnly" class="px-lg-3 px-1 py-1 py-lg-3" :class="{'transparent': phoneOnly, 'soft-shadow': pcOnly}" style="min-height: 50vh">
                <div class="d-flex flex-column flex-lg-row justify-space-between px-4 pt-3">
                    <div class="d-flex align-center">
                        <v-card-title style="line-height: normal" class="px-0 pt-0">
                            <v-icon color="primary" size="25" class="me-2">mdi-book-open-page-variant</v-icon>
                            <template v-if="selectedCourse">
                                <a class="breadcrumb-link" @click="selectedCourse = null">Materials</a>
                                <v-icon size="18" class="mx-1" color="grey">mdi-chevron-right</v-icon>
                                <span>{{ selectedCourse.name }}</span>
                            </template>
                            <span v-else>Materials</span>
                        </v-card-title>
                    </div>
                    <div class="d-flex align-center">
                        <v-btn v-if="selectedCourse" text small color="primary" @click="selectedCourse = null" class="me-2">
                            <v-icon left size="18">mdi-arrow-left</v-icon>
                            Back
                        </v-btn>
                        <v-text-field
                            v-if="!selectedCourse && courses && courses.length > 1"
                            v-model="searchQuery"
                            max-width="300"
                            type="search"
                            :append-icon="searchQuery ? 'mdi-close' : 'mdi-magnify'"
                            @click:append="searchQuery = ''"
                            hide-details
                            outlined
                            label="Search"
                            placeholder="Search courses"
                            dense
                            :style="pcOnly ? 'width: 250px;' : ''"
                        />
                    </div>
                </div>

                <v-card-text>
                    <!-- Loading State -->
                    <template v-if="loading">
                        <v-row>
                            <v-col v-for="i in 6" :key="'skel-'+i" cols="12" :sm="6" :md="4" :lg="3">
                                <v-skeleton-loader type="card-heading, list-item-two-line" class="rounded-lg" />
                            </v-col>
                        </v-row>
                    </template>

                    <!-- Folder Grid View -->
                    <transition name="fade" mode="out-in" v-else>
                        <div v-if="!selectedCourse" key="folders">
                            <v-row v-if="filteredCourses && filteredCourses.length">
                                <v-col v-for="course in filteredCourses" :key="course.id" cols="12" :sm="6" :md="4" :lg="3">
                                    <v-card
                                        class="folder-card rounded-lg pa-4"
                                        :elevation="hoveredFolder === course.id ? 4 : 1"
                                        @click="selectedCourse = course"
                                        @mouseenter="hoveredFolder = course.id"
                                        @mouseleave="hoveredFolder = null"
                                        style="cursor: pointer; transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);"
                                        :style="hoveredFolder === course.id ? 'transform: translateY(-2px);' : ''"
                                    >
                                        <div class="d-flex align-center">
                                            <div class="folder-icon-wrapper me-3">
                                                <v-icon size="44" color="amber darken-2">mdi-folder</v-icon>
                                            </div>
                                            <div class="flex-grow-1" style="overflow: hidden;">
                                                <div class="font-weight-bold strong-text--text text-body-2" style="line-height: 1.3; word-break: break-word;">{{ course.name }}</div>
                                                <div class="d-flex align-center mt-1">
                                                    <v-icon size="14" color="grey" class="me-1">mdi-file-pdf-box</v-icon>
                                                    <span class="muted--text text-caption">{{ course.materials_count }} file{{ course.materials_count !== 1 ? 's' : '' }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </v-card>
                                </v-col>
                            </v-row>
                            <div v-else-if="searchQuery && courses && courses.length" class="my-2">
                                <v-alert type="info" color="grey" text>
                                    <div class="strong-text--text">No courses match "{{ searchQuery }}"</div>
                                </v-alert>
                            </div>
                            <div v-else class="text-center py-10">
                                <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-book-open-page-variant-outline</v-icon>
                                <div class="text-h6 grey--text text--darken-1 mb-2">No materials available yet</div>
                                <div class="text-body-2 grey--text">Materials will appear here once your teacher uploads them for your courses.</div>
                            </div>
                        </div>

                        <!-- File List View (inside folder) -->
                        <div v-else key="files">
                            <v-list v-if="selectedCourse.materials && selectedCourse.materials.length" class="pa-0">
                                <v-list-item
                                    v-for="material in sortedMaterials"
                                    :key="material.id"
                                    class="file-item rounded-lg mb-2 px-4"
                                >
                                    <v-list-item-icon class="me-4 align-self-center my-3">
                                        <v-icon size="36" color="red darken-1">mdi-file-pdf-box</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content>
                                        <v-list-item-title class="font-weight-medium text-body-2">{{ material.title }}</v-list-item-title>
                                        <v-list-item-subtitle class="text-caption">
                                            {{ formatFileSize(material.file_size) }}
                                            <span class="mx-1">·</span>
                                            {{ formatDate(material.created_at) }}
                                        </v-list-item-subtitle>
                                    </v-list-item-content>
                                    <v-list-item-action>
                                        <v-btn
                                            depressed
                                            small
                                            color="primary"
                                            :loading="downloading === material.id"
                                            @click.stop="downloadMaterial(material)"
                                            class="text-none"
                                        >
                                            <v-icon left size="18">mdi-download</v-icon>
                                            Download
                                        </v-btn>
                                    </v-list-item-action>
                                </v-list-item>
                            </v-list>
                            <div v-else class="text-center py-10">
                                <v-icon size="48" color="grey lighten-1" class="mb-3">mdi-file-hidden</v-icon>
                                <div class="text-body-1 grey--text">No files in this course.</div>
                            </div>
                        </div>
                    </transition>
                </v-card-text>
            </v-card>
        </v-container>
    </div>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'

export default {
    name: 'Materials',
    data() {
        return {
            courses: [],
            loading: true,
            selectedCourse: null,
            downloading: null,
            hoveredFolder: null,
            searchQuery: '',
        }
    },
    computed: {
        ...GlobalComputed,
        filteredCourses(){
            if (!this.searchQuery) return this.courses;
            const query = this.searchQuery.toLowerCase();
            return this.courses.filter(c => c.name.toLowerCase().includes(query));
        },
        sortedMaterials(){
            if (!this.selectedCourse || !this.selectedCourse.materials) return [];
            return [...this.selectedCourse.materials].sort((a, b) => {
                return new Date(b.created_at) - new Date(a.created_at);
            });
        },
    },
    watch: {
        selectedCourse() {
            window.scrollTo(0, 0);
        }
    },
    mounted() {
        this.fetchMaterials();
    },
    methods: {
        ...GlobalMethods,
        fetchMaterials() {
            this.loading = true;
            window.axios.get('/materials').then(response => {
                this.courses = response.data;
            }).catch(() => {
                this.showMsg('Failed to load materials', 'error');
            }).finally(() => {
                this.loading = false;
            });
        },
        formatFileSize(bytes) {
            if (!bytes) return '0 B';
            const sizes = ['B', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(1024));
            return (bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0) + ' ' + sizes[i];
        },
        formatDate(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        },
        downloadMaterial(material) {
            this.downloading = material.id;
            window.axios.get(`/materials/${material.id}/download`, {
                responseType: 'blob',
            }).then(response => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', material.title + '.pdf');
                document.body.appendChild(link);
                link.click();
                link.remove();
                // Delay revocation to ensure download has started
                setTimeout(() => window.URL.revokeObjectURL(url), 2000);
                this.showMsg('File downloaded successfully', 'success');
            }).catch(() => {
                this.showMsg('Failed to download file', 'error');
            }).finally(() => {
                this.downloading = null;
            });
        },
    },
}

</script>

<style scoped>
.breadcrumb-link {
    color: inherit;
    text-decoration: none;
    cursor: pointer;
    opacity: 0.7;
    transition: opacity 0.2s ease;
}
.breadcrumb-link:hover {
    opacity: 1;
    text-decoration: underline;
}

.folder-card {
    border: 1px solid rgba(0, 0, 0, 0.08);
    min-height: 90px;
}
.folder-card:hover {
    border-color: rgba(0, 0, 0, 0.15);
}

.file-item {
    border: 1px solid rgba(0, 0, 0, 0.08);
    transition: all 0.2s ease;
}
.file-item:hover {
    background: rgba(0, 0, 0, 0.02);
    border-color: rgba(0, 0, 0, 0.12);
}

/* Fade transition for folder/file switching */
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter, .fade-leave-to {
    opacity: 0;
}
</style>
